import PropTypes from 'prop-types';
import React from 'react';
import TextEditable from '../../elements/editors/mui/TextEditable';
import {
  baseSelectStyles,
  getCurrentSelectedOption,
  getNewSelectionValue,
  SelectInput,
  SelectOption,
} from '../../elements/rselect/ReactSelectUtils';
import SortedSelect from '../../elements/rselect/SortedSelect';
import { FormConfigurationStep } from 'new/components/FormConfigurationStep/FormConfigurationStep';
import {
  Checkbox,
  checkboxClasses,
  FormControlLabel,
  formControlLabelClasses,
  Grid,
  InputBase,
  inputBaseClasses,
  styled,
} from '@mui/material';
import { filter, has, map, size } from 'lodash';
import UIUtils from '../../../lib/UIUtils';
import { SUPPORT_KEY_SINK_FORM_DYN_LUP } from 'js/constants/supportPageKeys';
import { Typography } from '@mui/material';
import { ResourcePopoverMenu } from 'new/elements/ResourcePopoverMenu/ResourcePopoverMenu';

import './DataMapConfigure.scss';

const styles = {
  nameLabel: {
    fontSize: 11,
    fontWeight: 500,
  },
  sampleCellTextField: {
    col: {
      height: 32,
      display: 'flex',
      flexGrow: 1,
      alignItems: 'center',
      paddingRight: 12,
      paddingLeft: 4,
    },
  },
};

const muiStyles = {
  checkboxIcon: {
    root: {
      color: '#FF7436',
      '&$checked': {
        color: '#FF7436',
      },
    },
    checked: {},
  },
  checkboxLabel: {
    root: {},
    label: {
      fontWeight: 'normal',
    },
  },
  inputBase: {
    root: {
      marginRight: 4,
    },
    input: {
      backgroundColor: '#fff',
      fontWeight: 400,
      width: '98%',
      padding: 4,
      fontSize: 12,
      color: '#273438',
      borderRadius: 4,
      border: '1px solid #000',
    },
  },
};

const StyledFormControlLabel = styled(FormControlLabel)({
  [`.${formControlLabelClasses.label}`]: muiStyles.checkboxLabel.label,
});
const StyledCheckbox = styled(Checkbox)({
  color: '#FF7436',
  [`&.${checkboxClasses.checked}`]: {
    color: '#FF7436',
  },
});
const StyledInputBase = styled(InputBase)({
  marginRight: 4,
  [`.${inputBaseClasses.input}`]: muiStyles.inputBase.input,
  [`&.${inputBaseClasses.multiline}`]: {
    [`& .${inputBaseClasses.input}`]: {
      whiteSpace: 'pre-wrap',
      overflow: 'auto',
      textOverflow: 'clip',
    },
  },
});

class EditDynamicDataMap extends React.Component {
  constructor(props) {
    super(props);

    this._renderFormFields = this._renderFormFields.bind(this);
    this._handleFormFieldUpdated = this._handleFormFieldUpdated.bind(this);
  }

  componentDidMount() {
    let { formType, sinkConfig, selectedConnection } = this.props;
    this.props.updateFormData(formType, {
      sinkConfig: {
        ...sinkConfig,
        sink_type: selectedConnection,
      },
    });
    if (this.props.setContextualSupport) {
      this.props.setContextualSupport(SUPPORT_KEY_SINK_FORM_DYN_LUP);
    }
  }

  componentWillUnmount() {
    if (this.props.unsetContextualSupport) {
      this.props.unsetContextualSupport(SUPPORT_KEY_SINK_FORM_DYN_LUP);
    }
  }

  render() {
    const { dataMapSettings } = this.props;
    const formContent = this._renderFormFields();

    return (
      <FormConfigurationStep
        connectionName={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="bodySmall"
              style={{
                paddingRight: 5,
              }}
            >
              Dynamic Lookup
            </Typography>

            <ResourcePopoverMenu
              isDestination
              nexsetId={this.props?.formData?.dataSetId || this.props?.dataSetId}
              connectorType={this.props.selectedConnection}
              disabledActions={['view-json-config']}
            />
          </div>
        }
        connectionIcon={
          <img
            width={16}
            height={16}
            src="https://cdn.nexla.io/ui/assets/data-sources/lookup.svg"
            alt="Configure Dynamic Lookup"
          />
        }
      >
        <div className="sink-dynamic-lookup">
          <header className="destination-stage-header">
            <h1 className="destination-stage-header-title">Configure Dynamic Lookup</h1>
            <p className="destination-stage-header-text">
              Dynamic lookups require at least two keys, one of which will be the primary key.
              Configure the name, primary key, and (optionally) default values for the lookup
              entries.
            </p>
          </header>
          <div className="sink-settings lookup lookup-name" data-testid={'lookup_name'}>
            <div className="lookup-name" style={styles.nameLabel}>
              {' '}
              Name *
            </div>
            <div style={{ flexGrow: 1 }}>
              <TextEditable
                id={'lookup_name'}
                name={'lookup_name'}
                rootStyle={{ width: '50%', height: 30 }}
                placeholder={'Set lookup name'}
                value={dataMapSettings['name'] || ''}
                onChange={(text) => {
                  this._handleFormFieldUpdated('name', text);
                }}
              />
            </div>
          </div>
          <div
            className="sink-settings lookup lookup-description"
            data-testid={'lookup_description'}
          >
            <div className="lookup-description" style={styles.nameLabel}>
              Description
            </div>
            <div style={{ flexGrow: 1 }}>
              <TextEditable
                id={'lookup_desc'}
                name={'lookup_desc'}
                rootStyle={{ width: '50%', height: 30 }}
                placeholder={'Set lookup description'}
                value={dataMapSettings['description'] || ''}
                onChange={(text) => {
                  this._handleFormFieldUpdated('description', text);
                }}
              />
            </div>
          </div>
          {formContent}
        </div>
      </FormConfigurationStep>
    );
  }

  _handleFormFieldUpdated(fieldName, fieldValue) {
    const { formType, dataMapSettings } = this.props;
    this.props.updateFormData(formType, {
      dataMapSettings: {
        ...dataMapSettings,
        [fieldName]: fieldValue,
      },
    });
  }

  _renderFormFields() {
    let { dataMapSettings, sinkConfigFields, dataSchema } = this.props;

    let formContent = map(sinkConfigFields, (field, index) => {
      if (field.name === 'data_defaults') {
        let renderContent = null;

        if (size(dataSchema) > 0) {
          let attrRows = dataSchema
            .filter((d) => dataMapSettings.map_primary_key !== d.__name)
            .map((d) => {
              let attrValue = has(dataMapSettings, `data_defaults.${d.__name}`)
                ? dataMapSettings['data_defaults.' + d.__name]
                : '';

              return (
                <Grid
                  container
                  key={'form-attributes-json' + d.__name}
                  className={'table-body-row'}
                >
                  <Grid item xs={5}>
                    <div
                      style={{
                        fontSize: 'var(--font-size-base-sm)',
                        color: '#4E636A',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {`${d.__name}`}
                    </div>
                  </Grid>
                  <Grid item xs={7}>
                    <div style={styles.sampleCellTextField.col}>
                      <TextEditable
                        className="dataMapSampleTextField"
                        key={'form-attributes-json-data_defaults.' + d.__name}
                        id={'form-attributes-json-data_defaults.' + d.__name}
                        name={'form-text-data_defaults.' + d.__name}
                        // displayIcon={true}
                        rootStyle={{ width: '100%' }}
                        placeholder={`Default value for ${d.__name}`}
                        value={attrValue}
                        onChange={(text) => {
                          this._handleFormFieldUpdated(`data_defaults.${d.__name}`, text);
                        }}
                      />
                    </div>
                  </Grid>
                </Grid>
              );
            });
          renderContent = (
            <div key={'grp-' + index}>
              <div className="full-width-table-label">Default Values</div>
              <div key={'grp-' + index} className={'full-width-table'}>
                <div className={'table-header'}>
                  <Grid container className={'table-header-row'}>
                    <Grid item xs={5}>
                      Lookup Key Name
                    </Grid>
                    <Grid item xs={7}>
                      Default Value
                    </Grid>
                  </Grid>
                </div>
                <div className={'table-body'}>{attrRows}</div>
              </div>
            </div>
          );
        }

        return renderContent;
      }

      if (!UIUtils.shouldRenderField(field, dataMapSettings)) {
        return null;
      }
      var fieldName = (
        <div className="field-label">
          {field.display_name} {field.required === true && !has(field, 'default') ? ' *' : null}
        </div>
      );

      var fieldInputContent;

      var defaultFieldValue =
        field.type === 'nonce'
          ? UIUtils.generateNonce()
          : has(field, 'default')
            ? field.default
            : null;

      var currentFieldValue = has(dataMapSettings, field.name)
        ? dataMapSettings[field.name]
        : defaultFieldValue;

      switch (field.inputType) {
        case 'select':
          {
            let selectionValues;
            if (field.attributes) {
              selectionValues = map(
                filter(this.props.dataSchema, (d) => d.__type !== 'object' && d.__type !== 'array'),
                (d) => ({
                  label: d.__name,
                  value: d.__name,
                }),
              );
            } else {
              selectionValues = map(field.values, (val) => {
                return { label: val, value: val };
              });
            }

            let currentSelectionValue = getCurrentSelectedOption(
              selectionValues,
              currentFieldValue,
            );

            fieldInputContent = (
              <div data-testid="lookup_pk_select" style={{ width: '50%', color: '#000' }}>
                <SortedSelect
                  options={selectionValues}
                  components={{ Input: SelectInput, Option: SelectOption }}
                  isClearable={false}
                  backspaceRemovesValue={false}
                  name="select"
                  id={`field-${field.name}`}
                  styles={baseSelectStyles}
                  value={currentSelectionValue}
                  isSearchable={true}
                  onChange={(newValue) => {
                    let newFieldValue = getNewSelectionValue(newValue, true, defaultFieldValue);
                    this._handleFormFieldUpdated(field.name, newFieldValue);
                  }}
                />
              </div>
            );
          }
          break;

        case 'attributes-json':
          break;
        case 'checkbox':
        case 'boolean':
          var checkboxStatus = currentFieldValue !== null ? currentFieldValue || false : false;
          fieldInputContent = (
            <div>
              <StyledFormControlLabel
                key={'checkbox' + field.name}
                label={field.description}
                control={
                  <StyledCheckbox
                    checked={checkboxStatus}
                    onChange={(event, isInputChecked) => {
                      this._handleFormFieldUpdated(field.name, isInputChecked);
                    }}
                  />
                }
              />
            </div>
          );

          break;

        case 'textarea':
          fieldInputContent = (
            <div style={{ width: '100%' }}>
              <StyledInputBase
                placeholder={field.description}
                fullWidth={true}
                multiline={true}
                minRows={2}
                maxRows={4}
                margin="dense"
                type="text"
                inputProps={{
                  autoComplete: 'new-password',
                }}
                value={currentFieldValue !== null ? currentFieldValue : ''}
                onChange={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  this._handleFormFieldUpdated(field.name, event.target.value);
                }}
              />
            </div>
          );

          break;

        case 'password':
        case 'text':
        default:
          var type = 'password' === field.inputType ? 'password' : 'text';
          fieldInputContent = (
            <div style={{ width: '100%' }}>
              <TextEditable
                id={'form-text' + field.name}
                name={'form-text' + field.name}
                type={type}
                disabled={this.props.credentialInputDisabled}
                placeholder={field.description}
                rootStyle={{ height: 30 }}
                value={currentFieldValue ? currentFieldValue || '' : ''}
                onChange={(text) => {
                  this._handleFormFieldUpdated(field.name, text);
                }}
              />
            </div>
          );

          break;
      }
      return (
        <div
          data-testid="lookup_pk"
          key={'grp-' + index}
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginTop: 19,
          }}
        >
          {fieldName}
          {fieldInputContent}
        </div>
      );
    });

    return formContent;
  }
}

EditDynamicDataMap.propTypes = {
  dataMapSettings: PropTypes.object,
  sinkConfigFields: PropTypes.array.isRequired,
};

export default EditDynamicDataMap;
